import { IconButton, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StatusPill from 'features/IRWorkstation/components/Common/StatusPill';
import { FUNDRAISING_COLUMNS } from 'features/IRWorkstation/constants/fundraisingColumns';
import { formatCurrency, formatDate } from 'utils/utils';

const PLACEHOLDER = '-';

// Sorting is server-side, so a column is only sortable when the API exposes a
// sort key for it (see FUNDRAISING_COLUMNS).
const renderers = {
	boolean: ({ value }) => (value ? 'Yes' : 'No'),
	currency: ({ value }) => formatCurrency(value) || PLACEHOLDER,
	// Salesforce Date fields arrive as date-only strings ("2026-09-15"), which
	// `new Date` parses as UTC midnight. Format in UTC so the calendar day is not
	// shifted back a day in timezones behind UTC (CR-6566).
	date: ({ value }) => formatDate(value, true) || PLACEHOLDER,
	stage: ({ value }) => (
		<StatusPill label={value || PLACEHOLDER} color='default' />
	),
};

// The LP name links to its LP360 page. A row with no account (or no link
// builder) falls back to plain text.
const renderAccountCell = ({ row, value }, getAccountLink) => {
	const link = row.account_id && getAccountLink?.(row);

	if (!value || !link) return value || PLACEHOLDER;

	return (
		<Link
			component={RouterLink}
			to={link.to}
			state={link.state}
			sx={{
				fontWeight: 500,
				color: 'primary.main',
				textDecoration: 'none',
				'&:hover': { textDecoration: 'underline' },
			}}
		>
			{value}
		</Link>
	);
};

// The action and account cells need handlers from the page, so the columns are
// built per render rather than being a module constant. `columnDefs` lets the
// LP360 table reuse the same renderers with its own column set.
const buildFundraisingGridColumns = (
	onMenuOpen,
	getAccountLink,
	columnDefs = FUNDRAISING_COLUMNS
) =>
	columnDefs.map(({ label, sortKey, field, type, minWidth }) => {
		const column = {
			field,
			headerName: label,
			sortable: Boolean(sortKey),
			minWidth,
			flex: 1,
			// The columns carry their own widths, so the header labels stay on one
			// line and the grid scrolls horizontally once they no longer fit
			disableColumnMenu: true,
		};

		if (type === 'actions') {
			return {
				...column,
				flex: 0,
				align: 'center',
				headerAlign: 'center',
				renderCell: ({ row }) => (
					<IconButton
						onClick={(event) => onMenuOpen(event, row)}
						size='small'
						color='primary'
						aria-label='more options'
						sx={{ cursor: 'pointer', '& *': { cursor: 'pointer !important' } }}
					>
						<MoreVertIcon />
					</IconButton>
				),
			};
		}

		if (type === 'account') {
			return {
				...column,
				renderCell: (params) => renderAccountCell(params, getAccountLink),
			};
		}

		if (renderers[type]) {
			return { ...column, renderCell: renderers[type] };
		}

		return {
			...column,
			renderCell: ({ value }) => value || PLACEHOLDER,
		};
	});

export default buildFundraisingGridColumns;
